import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import CohortTable from "./cohort-table";
import CohortCard from "./cohort-card/cohort-card";
import ApiCohortManagement from "../../../api/cohort-management";
import useNotification from "../../../components/notification/notification";
import "./styles.css";
import { Body } from "../../../components/main-body/body";
import { useForm } from "antd/es/form/Form";
import { Modal } from "antd";
import { useCan } from "../../../common/utils/usePermissions";
import debounce from "../../../common/utils/debounce";
import { useBulkSelection } from "../../../common/utils/useBulkSelection";
import BulkSelectionBar from "../../../components/table/bulk-selection-bar";
import useAdminEvents from "../../../hooks/useAdminEvents";

const DeleteModalComponent = ({
  item,
  handleOk,
  handleCancel,
  isModalOpen,
}) => {
  return (
    <Modal
      title={item?.value}
      open={isModalOpen}
      onOk={() => {
        handleOk(item.id, item?.value);
      }}
      onCancel={handleCancel}
      okButtonProps={{ danger: true }}
      okText="Delete"
      className="plan-page-action-modal"
    >
      <p>Are you sure you want to delete this cohort?</p>
    </Modal>
  );
};

const CohortManagement = () => {
  const [cohorts, setCohorts] = useState([]);
  const [isOpen, setOpen] = useState(false);
  const [editMode, setEditMode] = useState();
  const [selectedCohort, setSelectedCohort] = useState({});
  const [form] = useForm();
  const { t } = useTranslation();
  const canManageCohorts = useCan("settings.cohorts");
  const { openNotification } = useNotification();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [isBulkDeleting, setBulkDeleting] = useState(false);

  const {
    selectedKeys,
    clearSelection,
    getSelectionColumn,
    setSelectedKeys,
  } = useBulkSelection(cohorts, { rowKey: "id" });

  const tableRef = useRef(null);
  const [bulkLeft, setBulkLeft] = useState(null);

  useEffect(() => {
    const updatePosition = () => {
      if (!tableRef.current) return;
      const rect = tableRef.current.getBoundingClientRect();
      setBulkLeft(rect.left + rect.width / 2);
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("resize", updatePosition);
    };
  }, []);

  const handleBulkDelete = useCallback(async () => {
    if (selectedKeys.length === 0 || isBulkDeleting) return;

    setBulkDeleting(true);

    try {
      const resp = await ApiCohortManagement.removeBulk(selectedKeys);
      const { deletedIds = [], failed = [] } = resp?.data || {};
      const failedIds = failed.map((f) => f.id);

      if (deletedIds.length) {
        openNotification({
          type: "success",
          message: (
            <p>
              {deletedIds.length} {deletedIds.length > 1 ? "cohorts were" : "cohort was"} deleted
            </p>
          ),
        });
      }

      if (failedIds.length) {
        openNotification({
          type: "error",
          message: (
            <p>
              Unable to delete {failedIds.length} {failedIds.length > 1 ? "cohorts" : "cohort"}
            </p>
          ),
        });
      }

      if (!failedIds.length) {
        clearSelection();
      } else {
        setSelectedKeys(failedIds);
      }

      getCohorts(false, searchValue);
    } catch (e) {
      console.error(e);
      openNotification({
        type: "error",
        message: <p>Failed to delete cohorts</p>,
      });
    } finally {
      setBulkDeleting(false);
    }
  }, [
    selectedKeys,
    isBulkDeleting,
    clearSelection,
    setSelectedKeys,
    openNotification,
    searchValue,
  ]);

  const openCohortDrawer = (edit) => {
    if (selectedCohort?.value) {
      setSelectedCohort({});
    }
    if (edit) {
      setEditMode(true);
    }
    setOpen(true);
  };

  const closeCohortDrawer = () => {
    setSelectedCohort({});
    setEditMode(false);
    setOpen(false);
  };

  const getCohorts = (deselect, query = "") => {
    ApiCohortManagement.getCohorts({query}).then(({ data }) => {
      const { cohorts } = data || {};

      setCohorts(cohorts);
    });
  };

  useAdminEvents({
    onCohortChanged: () => getCohorts(false, searchValue),
  });

  const debouncedGetCohorts = useCallback(debounce(getCohorts, 400), [{}, selectedCohort.id]);

  const handleUpdateSearchValue = (value) => {
    setSearchValue(value);
    debouncedGetCohorts(false, value);
  };

  const headerProps = useMemo(
    () => ({
      title: "Cohort management",
      create: canManageCohorts
        ? {
            testID: "cohort-create",
            label: t("administration.cohort.create"),
            onClick: () => openCohortDrawer(true),
          }
        : undefined,
      search: {
        value: searchValue,
        onHandleChange: handleUpdateSearchValue,
      },
    }),
    [isOpen, selectedCohort, canManageCohorts]
  );

  useEffect(() => {
    getCohorts();
  }, []);

  const createCohort = (cohort) => {
    closeCohortDrawer();
    ApiCohortManagement.addCohort(cohort)
      .then(() => {
        getCohorts();
      })
      .then(() => {
        openNotification({
          type: "success",
          message: (
            <p>
              {t("administration.cohort.notification.created", {
                cohort: cohort.value,
              })}
            </p>
          ),
        });
      })
      .catch((e) => {
        openNotification({
          type: "error",
          message: (
            <p>
              {t(
                `administration.cohort.notification.${
                  e?.response?.data?.error || "error"
                }`
              )}
            </p>
          ),
        });
      });
  };

  const updateCohort = (id, cohort) => {
    closeCohortDrawer();
    ApiCohortManagement.updateCohort(id, cohort)
      .then(() => {
        getCohorts();
      })
      .then(() => {
        openNotification({
          type: "success",
          message: (
            <p>
              {t("administration.cohort.notification.updated", {
                cohort: cohort.value,
              })}
            </p>
          ),
        });
      })
      .catch((e) => {
        openNotification({
          type: "error",
          message: (
            <p>
              {t(
                `administration.cohort.notification.${
                  e?.response?.data?.error || "error"
                }`
              )}
            </p>
          ),
        });
      });
  };

  const deleteCohort = (id, value) => {
    setIsDeleteModalOpen(false);
    closeCohortDrawer();
    ApiCohortManagement.deleteCohort(id)
      .then(() => {
        getCohorts();
      })
      .then(() => {
        openNotification({
          type: "success",
          message: (
            <p>
              {t("administration.cohort.notification.deleted", {
                cohort: value,
              })}
            </p>
          ),
        });
      })
      .catch((e) => {
        openNotification({
          type: "error",
          message: (
            <p>
              {t(
                `administration.cohort.notification.${
                  e?.response?.data?.error || "error"
                }`
              )}
            </p>
          ),
        });
      })
      .finally(() => {
        getCohorts();
      });
  };

  const handleSelectCohort = (cohort) => {
    setSelectedCohort(cohort);
    setOpen(true);
  };

  const toEditMode = () => {
    setEditMode(true);
  };

  const onHandleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  const onCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  useEffect(() => {
    form.resetFields();
  }, [selectedCohort, form]);

  return (
    <Body header={headerProps}>
      <CohortTable
        data={cohorts}
        onHandleRowClick={handleSelectCohort}
        selectionColumn={getSelectionColumn()}
        containerRef={tableRef}
        after={
          <BulkSelectionBar
            left={bulkLeft}
            count={selectedKeys.length}
            onClear={clearSelection}
            onDelete={handleBulkDelete}
            loading={isBulkDeleting}
          />
        }
      />
      <CohortCard
        cohort={selectedCohort}
        isOpen={isOpen}
        close={closeCohortDrawer}
        editMode={editMode}
        toEditMode={toEditMode}
        createCohort={createCohort}
        updateCohort={updateCohort}
        deleteCohort={onHandleDelete}
        form={form}
      />
      <DeleteModalComponent
        item={selectedCohort}
        isModalOpen={isDeleteModalOpen}
        handleCancel={onCloseDeleteModal}
        handleOk={deleteCohort}
      />
    </Body>
  );
};

export default CohortManagement;
